import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { api } from "~/lib/api";
import { getT } from "~/lib/i18n";
import { useProductsStore } from "~/stores/productsStore";
import type { Order, ShippingMeta } from "~/types";
import { useToastStore } from "~/stores/toastStore";

const NP_FLAT_COST = 80;
const LOGIST_MIN = 2000;

interface CartItem {
  id: string;
  qty: number;
}
type ShippingMethod = "logist" | "np" | null;

interface CartState {
  items: CartItem[];
  shipping: ShippingMethod;
  shippingMeta: ShippingMeta | null;

  count: () => number;
  subtotal: () => number;
  shippingCost: () => number;
  total: () => number;
  shortfall: () => number;
  canCheckout: () => boolean;

  add: (id: string, qty?: number) => void;
  setQty: (id: string, qty: number) => void;
  remove: (id: string) => void;
  clear: () => void;
  setShipping: (method: Exclude<ShippingMethod, null>, meta?: ShippingMeta) => void;
  replaceItems: (items: CartItem[]) => void;
  checkout: () => Promise<Order>;
}

const defaultMeta = (m: Exclude<ShippingMethod, null>): ShippingMeta =>
  m === "logist"
    ? { method: "logist", address: "sklad" }
    : { method: "np", city: "Київ", branch: "1" };

function priceOf(id: string): number {
  return useProductsStore.getState().byId(id)?.price ?? 0;
}

/**
 * Spec § 9 (cartStore): any items-mutating action that drops subtotal
 * below 2000 ₴ while shipping is 'logist' MUST auto-switch to NP and
 * fire a localized toast. Called from setQty / remove / replaceItems.
 * clear() resets shipping wholesale to a neutral state, so it skips
 * this helper.
 */
function enforceLogistMinimum(
  get: () => CartState,
  set: (partial: Partial<CartState>) => void,
): void {
  const st = get();
  if (st.shipping === "logist" && st.subtotal() < LOGIST_MIN) {
    set({ shipping: "np", shippingMeta: defaultMeta("np") });
    useToastStore.getState().show(getT("np_switched_toast"));
  }
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      shipping: null,
      shippingMeta: null,

      count: () => get().items.reduce((s, i) => s + i.qty, 0),
      subtotal: () => get().items.reduce((s, i) => s + priceOf(i.id) * i.qty, 0),
      shippingCost: () => (get().shipping === "np" ? NP_FLAT_COST : 0),
      total: () => get().subtotal() + get().shippingCost(),
      shortfall: () => {
        const { shipping, items } = get();
        if (shipping !== "logist" || items.length === 0) return 0;
        return Math.max(0, LOGIST_MIN - get().subtotal());
      },
      canCheckout: () => {
        const { items, shipping } = get();
        if (items.length === 0 || shipping === null) return false;
        if (shipping === "logist" && get().subtotal() < LOGIST_MIN) return false;
        return true;
      },

      add: (id, qty = 1) => {
        set((s) => {
          const found = s.items.find((i) => i.id === id);
          if (found)
            return {
              items: s.items.map((i) => (i.id === id ? { ...i, qty: i.qty + qty } : i)),
            };
          return { items: [...s.items, { id, qty }] };
        });
      },

      setQty: (id, qty) => {
        set((s) => ({
          items:
            qty <= 0
              ? s.items.filter((i) => i.id !== id)
              : s.items.map((i) => (i.id === id ? { ...i, qty } : i)),
        }));
        enforceLogistMinimum(get, set);
      },

      remove: (id) => {
        set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
        enforceLogistMinimum(get, set);
      },

      clear: () => {
        set({ items: [], shipping: null, shippingMeta: null });
      },

      setShipping: (method, meta) =>
        set({ shipping: method, shippingMeta: meta ?? defaultMeta(method) }),

      replaceItems: (items) => {
        set({ items });
        enforceLogistMinimum(get, set);
      },

      checkout: async () => {
        const { items, shipping, shippingMeta, subtotal, shippingCost } = get();
        if (!shipping || !shippingMeta) throw new Error("shipping not selected");
        if (!get().canCheckout()) throw new Error("cannot checkout: 2000₴ shortfall");
        const order = await api.createOrder({
          items: items.map((i) => ({ p: i.id, q: i.qty })),
          shipping: shippingMeta,
          subtotal: subtotal(),
          shippingCost: shippingCost(),
        });
        set({ items: [], shipping: null, shippingMeta: null });
        return order;
      },
    }),
    {
      name: "fnd.cart",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        items: s.items,
        shipping: s.shipping,
        shippingMeta: s.shippingMeta,
      }),
    },
  ),
);
